import { useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { projectPMC } from '@/lib/analytics-calculations';
import { format, parseISO } from 'date-fns';

interface CTLProjectionChartProps {
  historicalData: Array<{ date: string; ctl: number; atl: number; tsb: number }>;
}

export function CTLProjectionChart({ historicalData }: CTLProjectionChartProps) {
  const [weeks, setWeeks] = useState(8);
  const [plannedTss, setPlannedTss] = useState(0);

  const chartData = useMemo(() => {
    if (!historicalData.length) return [];
    const last = historicalData[historicalData.length - 1];
    const lastDate = new Date(last.date);
    const projected = projectPMC(last.ctl, last.atl, lastDate, weeks, plannedTss);
    const tail = historicalData.slice(-30).map((d) => ({
      date: d.date, dateLabel: fmt(d.date), ctl: d.ctl, tsb: d.tsb, projected: false,
    }));
    const proj = projected.map((d) => ({
      date: d.date, dateLabel: fmt(d.date), ctl: d.ctl, tsb: d.tsb, projected: true,
    }));
    return [...tail, ...proj];
  }, [historicalData, weeks, plannedTss]);

  const projectionStart = historicalData.length ? fmt(historicalData[historicalData.length - 1].date) : undefined;

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold">CTL / TSB Projection</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono text-muted-foreground">Weeks:</span>
          {[4, 8, 12].map((w) => (
            <Button key={w} variant={weeks === w ? 'default' : 'outline'} size="sm" className="h-7 text-xs px-2.5" onClick={() => setWeeks(w)}>{w}</Button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-[10px] font-mono text-muted-foreground">Daily TSS:</span>
        {[0, 40, 60, 80].map((t) => (
          <Button key={t} variant={plannedTss === t ? 'default' : 'outline'} size="sm" className="h-7 text-xs px-2.5" onClick={() => setPlannedTss(t)}>
            {t === 0 ? 'Rest' : t}
          </Button>
        ))}
      </div>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} vertical={false} />
            <XAxis dataKey="dateLabel" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} interval="preserveStartEnd" />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} width={35} />
            <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
            {projectionStart && <ReferenceLine x={projectionStart} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" label={{ value: 'Now', fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />}
            <ReferenceLine y={0} stroke="hsl(var(--border))" strokeDasharray="3 3" />
            <Line dataKey="ctl" stroke="hsl(var(--chart-1))" strokeWidth={1.5} dot={false} name="CTL (Fitness)" />
            <Line dataKey="tsb" stroke="hsl(var(--chart-2))" strokeWidth={1.5} dot={false} name="TSB (Form)" strokeDasharray="4 2" />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] font-mono text-muted-foreground text-center mt-2">Dashed = projected · Adjust planned TSS to model scenarios</p>
    </div>
  );
}

function fmt(dateStr: string): string {
  try { return format(parseISO(dateStr), 'MMM d'); } catch { return dateStr.slice(5); }
}
